import { Suspense, lazy, useState } from 'react'
import { KeycloakAdminService } from '@nv/auth'
import { Loader, NVAlertsBanner, Tabs } from '@nv/basic-components'
import { ScrollToTop, VoterInactivityTimer } from '@nv/components'
import {
  AdminUserProvider,
  AriaAnnouncerProvider,
  ConfigurationContext,
  LoginFlowContext,
  NVAlertsProvider,
  RegistrationUserProvider,
  SessionTimerProvider,
  ThemeProvider,
  VoterOnboardingContext,
  VoterUserProvider,
  useAdminUserContext,
} from '@nv/contexts'
import { useAdminKeycloakAuth, useGetEvent, useVoterKeyCloakAuth } from '@nv/hooks'
import Banner from '@nv/images/banners/portal-banner.png'
import { AdminElectorsList, UserLoginOneKey } from '@nv/pages'
import { NvRoute, VerifyUserGuard } from '@nv/router'
import { nxConfig } from '@nv/utils'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes as Switch, useParams } from 'react-router-dom'
import getConfigurations from '../configurations'
import { AdminElectionConfig } from '../pages/admin/admin_election_config/AdminElectionConfig'
import { fnTheme } from '../Theme-fn'

const AddElectors = lazy(() => import('@nv/images/nav-icons/add-electors').then((mod: any) => ({ default: mod.AddElectors })))
const Elections = lazy(() => import('@nv/images/nav-icons/elections').then((mod: any) => ({ default: mod.Elections })))
const ElectorsList = lazy(() => import('@nv/images/nav-icons/electors-list').then((mod: any) => ({ default: mod.ElectorsList })))
const MyAccount = lazy(() => import('@nv/images/nav-icons/my-account').then((mod: any) => ({ default: mod.MyAccount })))
const Reports = lazy(() => import('@nv/images/nav-icons/reports').then((mod: any) => ({ default: mod.Reports })))
const Users = lazy(() => import('@nv/images/nav-icons/users').then((mod: any) => ({ default: mod.Users })))
const HelpAndSupportLogo = lazy(() =>
  import('@nv/images/nav-icons/help-and-support').then((mod: any) => ({ default: mod.HelpAndSupportLogo })),
)

const AdminEventDetailsHeader = lazy(() => import('@nv/components/lib/admin_event_details_header/AdminEventDetailsHeader'))
const BackgroundBanner = lazy(() => import('@nv/components/lib/background_banner/BackgroundBanner'))
const BottomBar = lazy(() => import('@nv/components/lib/navbars/bottombar/BottomBar'))
const ExtendSessionBanner = lazy(() => import('@nv/components/lib/extend_session_banner/ExtendSessionBanner'))
const LoginTopBar = lazy(() => import('@nv/components/lib/navbars/login_topbar/LoginTopBar'))
const SideBar = lazy(() => import('@nv/components/lib/navbars/sidebar/SideBar'))
const TopBar = lazy(() => import('@nv/components/lib/navbars/topbar/TopBar'))

const AdminAddElector = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminAddElector })))
const AdminEditElector = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminEditElector })))
const AdminEligibleDetails = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminEligibleDetails })))
const AdminEventElectorsList = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminEventElectorsList })))
const AdminEventOverview = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminEventOverview })))
const AdminLogin = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminLogin })))
const AdminMyAccount = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminMyAccount })))
const AdminRegisteredOnlineList = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminRegisteredOnlineList })))
const AdminReports = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminReports })))
const AdminSFTP = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminSFTP })))
const AdminUserList = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminUserList })))
const AdminVoterDetails = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AdminVoterDetails })))
const AuthAppSetup = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AuthAppSetup })))
const AuthCodeMethod = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AuthCodeMethod })))
const AuthVerify = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.AuthVerify })))
const ChangePassword = lazy(() => import('@nv/pages/lib/change_password/ChangePassword'))
const ChangeQRCode = lazy(() => import('@nv/pages/lib/change_qr_code/ChangeQRCode'))
const HelpAndSupport = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.HelpAndSupport })))
const OnBoardingAuthMethod = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.OnBoardingAuthMethod })))
const OnBoardingCreatePassword = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.OnBoardingCreatePassword })))
const OnBoardingPhoneVerify = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.OnBoardingPhoneVerify })))
const RegistrationStep3 = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.RegistrationStep3 })))
const ResetPassword = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.ResetPassword })))
const UserLogin = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.UserLogin })))
const VoterEventDetails = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.VoterEventDetails })))
const VoterMyAccount = lazy(() => import('@nv/pages').then((mod: any) => ({ default: mod.VoterMyAccount })))

// PUBLIC ROUTES:
const RegistrationLanding = lazy(() =>
  import('../pages/public/registration_landing/RegistrationLanding').then((mod: any) => ({ default: mod.RegistrationLanding })),
)
const RegistrationStep1Landing = lazy(() =>
  import('../pages/public/registration_step1_landing/RegistrationStep1Landing').then((mod: any) => ({
    default: mod.RegistrationStep1Landing,
  })),
)
const RegistrationStep2Landing = lazy(() =>
  import('../pages/public/registration_step2_landing/RegistrationStep2Landing').then((mod: any) => ({
    default: mod.RegistrationStep2Landing,
  })),
)

// VOTER ROUTES:
const VoterEventsLanding = lazy(() =>
  import('../pages/voter/voter_events_landing/VoterEventsLanding').then((mod: any) => ({ default: mod.VoterEventsLanding })),
)

// ADMINROUTES:
const AdminEventsLanding = lazy(() =>
  import('../pages/admin/admin_events_landing/AdminEventsLanding').then((mod: any) => ({ default: mod.AdminEventsLanding })),
)
const AdminElectorsListLanding = lazy(() =>
  import('../pages/admin/admin_eligible_list_landing/AdminEligibleListLanding').then((mod: any) => ({
    default: mod.AdminElectorsListLanding,
  })),
)

export const VoterKeyCloakAuth = () => {
  useVoterKeyCloakAuth()
  return null
}
export const AdminKeycloakAuth = () => {
  useAdminKeycloakAuth()
  return null
}

const VoterLayout = ({ children }) => {
  const { t } = useTranslation()
  const voterLinks = {
    elections: {
      path: '/voting/voter/dashboard',
      tabLabel: t('pages.events'),
      icon: Elections,
      hasPermissionToView: true,
    },
    help: {
      path: '/voting/voter/help-support',
      tabLabel: t('pages.helpAndSupport'),
      icon: HelpAndSupportLogo,
      hasPermissionToView: true,
    },
    myAccount: {
      path: '/voting/voter/my-account',
      tabLabel: t('pages.myAccount'),
      icon: MyAccount,
      hasPermissionToView: true,
    },
  }
  return (
    <>
      <VoterInactivityTimer />
      <TopBar links={voterLinks} />
      <div className='main-content-container'>
        <SideBar links={voterLinks} />
        <main>
          <NVAlertsBanner />
          {children}
        </main>
        <BottomBar links={voterLinks} />
      </div>
    </>
  )
}

const AdminLayout = ({ children, tabs }: any) => {
  const { t } = useTranslation()
  useAdminUserContext()

  const adminLinks = {
    events: {
      path: '/admin/dashboard',
      tabLabel: t('pages.events'),
      icon: Elections,
      hasPermissionToView:
        KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isEventModuleAdmin() || KeycloakAdminService.isMonitoringAdmin(),
    },
    electorsList: {
      path: '/admin/electors-list',
      tabLabel: t('pages.electorsList'),
      icon: ElectorsList,
      hasPermissionToView:
        KeycloakAdminService.isSuperAdmin() ||
        KeycloakAdminService.isEventModuleAdmin() ||
        KeycloakAdminService.isContactInfoRoleAdmin() ||
        KeycloakAdminService.isPollWorkerRoleAdmin(),
    },
    // adminAddElectors: {
    //   path: '/admin/admin-add-electors',
    //   tabLabel: t('pages.addElectors'),
    //   icon: AddElectors,
    //   hasPermissionToView:
    //     (KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isContactInfoRoleAdmin()) &&
    //     !KeycloakAdminService.isEventAdminDeveloper(),
    // },
    adminUsers: {
      path: '/admin/admin-users',
      tabLabel: t('pages.adminUsers'),
      icon: Users,
      hasPermissionToView: KeycloakAdminService.isSuperAdmin(),
    },
    HelpAndSupport: {
      path: '/admin/help-support',
      tabLabel: t('pages.helpAndSupport'),
      icon: HelpAndSupportLogo,
      hasPermissionToView: KeycloakAdminService.isSuperAdmin(),
    },
    myAccount: {
      path: '/admin/my-account',
      tabLabel: t('pages.myAccount'),
      icon: MyAccount,
      hasPermissionToView:
        KeycloakAdminService.isSuperAdmin() ||
        KeycloakAdminService.isEventModuleAdmin() ||
        KeycloakAdminService.isMonitoringAdmin() ||
        KeycloakAdminService.isContactInfoRoleAdmin(),
    },
    reports: {
      path: '/admin/reports',
      tabLabel: t('pages.reports'),
      icon: Reports,
      hasPermissionToView: KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isMonitoringAdmin(),
    },
    sftp: {
      path: '/admin/sftp',
      tabLabel: t('pages.sftp'),
      icon: Reports,
      hasPermissionToView: KeycloakAdminService.hasRole('neuvote-super-admin'),
    },
  }

  return (
    <>
      <TopBar links={adminLinks} />
      <div className='main-content-container'>
        <SideBar links={adminLinks} />
        <main>
          <NVAlertsBanner />
          <ExtendSessionBanner />
          {tabs}
          {children}
        </main>
      </div>
    </>
  )
}

const AdminEventTabLayout = () => {
  const { eventId } = useParams()
  useAdminUserContext()
  const event = useGetEvent(eventId)
  const { t } = useTranslation()
  return (
    <>
      <AdminEventDetailsHeader eventId={eventId} />
      <Tabs
        tabs={[
          {
            label: t('events.labels.overview'),
            path: `/admin/dashboard/events-overview/${eventId}`,
          },
          {
            label: t('pages.electorsList'),
            path: `/admin/dashboard/electors-list/${eventId}/${event?.tiviId}`,
          },
        ]}
      />
    </>
  )
}

const AdmimElectorsListTabLayout = () => {
  const { t } = useTranslation()

  const tabs = [
    {
      label: t('admin.electorsList.electorsList'),
      path: `/admin/electors-list`,
    },
  ]
  if (!nxConfig.useDatafix && (KeycloakAdminService.isSuperAdmin() || KeycloakAdminService.isContactInfoRoleAdmin())) {
    tabs.push({
      label: t('registration.registeredOnline'),
      path: `/admin/electors-list/registered-online`,
    })
  }
  if (KeycloakAdminService.isSuperAdmin()) {
    tabs.push({
      label: t('events.electionConfig'),
      path: `/admin/election-config`,
    })
  }
  useAdminUserContext()
  return <Tabs tabs={tabs} />
}

const PublicLayout = ({ children }) => {
  const [authMethod, setAuthMethod] = useState(null)
  const reCaptchaKey = nxConfig.RECAPTCHA_SITE_KEY

  return (
    <GoogleReCaptchaProvider reCaptchaKey={reCaptchaKey}>
      <VoterOnboardingContext.Provider value={{ authMethod, setAuthMethod }}>
        <LoginTopBar />
        <BackgroundBanner banner={Banner} />
        <main>
          <NVAlertsBanner />
          {children}
        </main>
      </VoterOnboardingContext.Provider>
    </GoogleReCaptchaProvider>
  )
}

const RoutesWrapper = () => {
  const [loginFlow, setLoginFlow] = useState('Login')
  const { t } = useTranslation()
  const configurations = getConfigurations({ t })

  return (
    <SessionTimerProvider>
      <VoterUserProvider>
        <AdminUserProvider>
          <AriaAnnouncerProvider>
            <LoginFlowContext.Provider value={{ loginFlow, setLoginFlow }}>
              <ConfigurationContext.Provider value={configurations}>
                <ThemeProvider initialTheme={fnTheme}>
                  <NVAlertsProvider>
                    <ScrollToTop />
                    <Routes />
                  </NVAlertsProvider>
                </ThemeProvider>
              </ConfigurationContext.Provider>
            </LoginFlowContext.Provider>
          </AriaAnnouncerProvider>
        </AdminUserProvider>
      </VoterUserProvider>
    </SessionTimerProvider>
  )
}

const Routes = () => {
  // prettier-ignore
  return (
    <>
      <Switch>
        <Route path={'/admin/*'} element={<AdminKeycloakAuth />} />
        <Route path='*' element={<VoterKeyCloakAuth/>} />
      </Switch>

      <Suspense fallback={<Loader />}>
        <Switch>
          {/* ADMIN ROUTES */}
          <Route path='/admin/dashboard' element={<NvRoute render={() => <VerifyUserGuard component={AdminEventsLanding} redirectURL={'/admin/login'} />} layout={AdminLayout} />}/>
          <Route path='/admin/events' element={<NvRoute render={() => <VerifyUserGuard component={AdminEventsLanding} redirectURL={'/admin/login'} />} layout={AdminLayout} />}/>
         {nxConfig.useDatafix ?
          <>
          <Route path='/admin/electors-list' element={<NvRoute render={() => <VerifyUserGuard component={AdminElectorsList} redirectURL={'/admin/login'} />} layout={<AdminLayout tabs={<AdmimElectorsListTabLayout />}/>} />}/>
          </> :
          <>
            <Route path='/admin/electors-list' element={<NvRoute render={() => <VerifyUserGuard component={AdminElectorsListLanding} redirectURL={'/admin/login'} />} layout={<AdminLayout tabs={<AdmimElectorsListTabLayout />} />} />}/>
            <Route path='/admin/electors-list/registered-online' element={<NvRoute render={() => <VerifyUserGuard component={AdminRegisteredOnlineList} redirectURL={'/admin/login'} />} layout={<AdminLayout tabs={<AdmimElectorsListTabLayout />} />} />}/>
          </>}
          <Route path='/admin/election-config' element={<NvRoute render={() => <VerifyUserGuard component={AdminElectionConfig} redirectURL={'/admin/login'} />} layout={<AdminLayout tabs={<AdmimElectorsListTabLayout />} />} />}/>
          <Route path='/admin/registrant-details/:voterId' element={<NvRoute render={() => <VerifyUserGuard component={AdminVoterDetails} redirectURL={'/admin/login'} />} layout={AdminLayout} />}/>
          <Route path='/admin/elector-edit/:voterId' element={<NvRoute render={() => <VerifyUserGuard component={AdminEditElector} redirectURL={'/admin/login'} />} layout={AdminLayout} />}/>
          <Route path='/admin/elector-details/:voterId' element={<NvRoute render={() => <VerifyUserGuard component={AdminEligibleDetails} redirectURL={'/admin/login'} />} layout={AdminLayout} />}/>

          <Route path='/admin/admin-add-electors' element={<NvRoute render={() => <VerifyUserGuard component={AdminAddElector} redirectURL={'/admin/login'} />} layout={AdminLayout} />}/>
          <Route path='/admin/admin-users' element={<NvRoute render={() => <VerifyUserGuard component={AdminUserList} redirectURL={'/admin/login'} />} layout={AdminLayout} />}/>
          <Route path='/admin/my-account' element={<NvRoute render={() => <VerifyUserGuard component={AdminMyAccount} redirectURL={'/admin/login'} />} layout={AdminLayout} />}/>
          <Route path='/admin/change-qr-code' element={<NvRoute render={() => <VerifyUserGuard component={() => <ChangeQRCode admin />} redirectURL={'/admin/login'} />} layout={AdminLayout} />} />
          <Route path='/admin/help-support' element={<NvRoute render={() => <VerifyUserGuard component={HelpAndSupport} redirectURL={'/admin/login'} />} layout={AdminLayout} />}/>
          <Route path='/admin/sftp' element={<NvRoute render={() => <VerifyUserGuard component={AdminSFTP} redirectURL={'/admin/login'} />} layout={AdminLayout} />}/>
          <Route path='/admin/change-password' element={<NvRoute render={() => <VerifyUserGuard component={() => <ChangePassword isAdmin={true} />} redirectURL={'/admin/login'} />} layout={AdminLayout} />}/>
          <Route path='/admin/reports' element={<NvRoute render={() => <VerifyUserGuard component={AdminReports} redirectURL={'/admin/login'} />} layout={AdminLayout} />}/>
          <Route path='/admin/dashboard/events-overview/:eventId' element={<NvRoute render={() => <VerifyUserGuard component={AdminEventOverview} redirectURL={'/admin/login'} />} layout={<AdminLayout tabs={<AdminEventTabLayout />} />} />}/>
          <Route path='/admin/dashboard/electors-list/:eventId/:tiviId' element={<NvRoute render={() => <VerifyUserGuard component={AdminEventElectorsList} redirectURL={'/admin/login'} />} layout={<AdminLayout tabs={<AdminEventTabLayout />} />} />}/>

          {/* VOTER ROUTES */}
          <Route path='/voting/voter/dashboard' element={<NvRoute render={() => <VerifyUserGuard component={VoterEventsLanding} redirectURL={'/'} />} layout={VoterLayout} />}/>
          <Route path='/voting/voter/my-account' element={<NvRoute render={() => <VerifyUserGuard component={VoterMyAccount} redirectURL={'/'} />} layout={VoterLayout} />}/>
          <Route path='/voting/voter/help-support' element={<NvRoute render={() => <VerifyUserGuard component={HelpAndSupport} redirectURL={'/'} />} layout={VoterLayout} />}/>
          <Route path='/voting/voter/change-qr-code' element={<NvRoute render={() => <VerifyUserGuard component={ChangeQRCode} redirectURL={'/'} />} layout={VoterLayout} />}/>
          <Route path='/voting/voter/change-password' element={<NvRoute render={() => <VerifyUserGuard component={ChangePassword} redirectURL={'/'} />} layout={VoterLayout} />}/>
          <Route path='/voting/voter/event-details/:eventId' element={<NvRoute render={() => <VerifyUserGuard component={VoterEventDetails} redirectURL={'/'} />} layout={VoterLayout} />}/>

          {/* PUBLIC ROUTES */}
          <Route path='/voting/voter/login' element={<NvRoute component={UserLoginOneKey} layout={PublicLayout} />}/>
          <Route path='/voting/voter-login' element={<NvRoute component={UserLoginOneKey} layout={PublicLayout} />}/>
          <Route path='/' element={<NvRoute component={UserLoginOneKey} layout={PublicLayout} />}/>
          <Route path='/registration/step1' element={<RegistrationUserProvider><NvRoute component={RegistrationStep1Landing} layout={PublicLayout} /></RegistrationUserProvider>}/>
          <Route path='/registration/step2' element={<RegistrationUserProvider><NvRoute component={RegistrationStep2Landing} layout={PublicLayout} /></RegistrationUserProvider>}/>
          <Route path='/registration/step3' element={<RegistrationUserProvider><NvRoute component={RegistrationStep3} layout={PublicLayout} /></RegistrationUserProvider>}/>

          <Route path='/voting/voter-verify/select-verifier' element={<NvRoute component={AuthCodeMethod} layout={PublicLayout} />}/>
          <Route path='/voting/voter-verify/verify' element={<NvRoute component={AuthVerify} layout={PublicLayout} />}/>
          <Route path='/voting/voter-auth/reset-password' element={<NvRoute component={ResetPassword} layout={PublicLayout} />}/>
          <Route path='/voting/voter-auth/create-password' element={<NvRoute component={OnBoardingCreatePassword} layout={PublicLayout} />}/>
          <Route path='/voting/voter-onboarding/phone-verify' element={<NvRoute component={OnBoardingPhoneVerify} layout={PublicLayout} />} />
          <Route path='/voting/voter-onboarding/auth-method' element={<NvRoute component={OnBoardingAuthMethod} layout={PublicLayout} />}/>
          <Route path='/voting/voter-auth/auth-app-setup' element={<NvRoute component={AuthAppSetup} layout={PublicLayout} />}/>
          <Route path='/voting/voter-auth/change-password' element={<NvRoute component={ChangePassword} layout={PublicLayout} />}/>

          <Route path='/admin' element={<NvRoute component={AdminLogin} layout={PublicLayout} />} />
          <Route path='/admin/login' element={<NvRoute component={AdminLogin} layout={PublicLayout} />}/>
          <Route path='/admin/auth/verify' element={<NvRoute component={AuthVerify} layout={PublicLayout} />}/>
          <Route path='/admin/auth/auth-app-setup' element={<NvRoute component={AuthAppSetup} layout={PublicLayout} />}/>
          <Route path='/admin/auth/change-password' element={<NvRoute component={() => <ChangePassword isAdmin={true} />} layout={PublicLayout} />}/>
          <Route path='/admin/auth/reset-password' element={<NvRoute component={ResetPassword} layout={PublicLayout} />}/>

          <Route path='/voting/voter/*' element={<NvRoute render={() => <Navigate to='/voting/voter/dashboard' />} />} />
          <Route path='/admin/*' element={<NvRoute render={() => <Navigate to='/admin/dashboard' />} />} />
          <Route path='*' element={<Navigate to='/' />} />
        </Switch>
      </Suspense>
    </>
  )
}
export { RoutesWrapper as Routes }
